import React, { useMemo, useState } from 'react';
import { ScrollTextIcon, MapPinIcon, DollarSignIcon, ShoppingCartIcon, LogInIcon, LogOutIcon, SearchIcon, FilterIcon, CalendarIcon, UserIcon, NavigationIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { PageHeader } from '../components/ui/PageHeader';
import { useAuth } from '../context/AuthContext';
import { ActivityLog } from '../types';
function getActionIcon(action: string) {
  const a = action.toLowerCase();
  if (a.includes('visita')) return <MapPinIcon className="w-4 h-4" data-id="element-364" data-mp="1hrrro866mvuu" />;
  if (a.includes('cobro') || a.includes('pago')) return <DollarSignIcon className="w-4 h-4" data-id="element-365" data-mp="1ctqda0jpbkpp" />;
  if (a.includes('venta') || a.includes('pedido')) return <ShoppingCartIcon className="w-4 h-4" data-id="element-366" data-mp="17tu5dq7vv94v" />;
  if (a.includes('inicio de sesión')) return <LogInIcon className="w-4 h-4" data-id="element-367" data-mp="12v0qpugv1c34" />;
  if (a.includes('cierre de sesión')) return <LogOutIcon className="w-4 h-4" data-id="element-368" data-mp="12x3qv7bq8llk" />;
  if (a.includes('factura') || a.includes('nota de crédito') || a.includes('nc')) return <DollarSignIcon className="w-4 h-4" data-id="element-369" data-mp="35krkozjyn9n7" />;
  if (a.includes('producto') || a.includes('inventario')) return <ShoppingCartIcon className="w-4 h-4" data-id="element-370" data-mp="2qwdkyxhuc1av" />;
  if (a.includes('planificación') || a.includes('televentas')) return <ClockIcon className="w-4 h-4" data-id="element-371" data-mp="2vuoqwx50lrnt" />;
  if (a.includes('usuario') || a.includes('rol') || a.includes('comision')) return <UserIcon className="w-4 h-4" data-id="element-372" data-mp="3jlu9vl69502c" />;
  if (a.includes('farmacia') || a.includes('caja vacía')) return <MapPinIcon className="w-4 h-4" data-id="element-373" data-mp="3oiugpyaxe6ui" />;
  return <ScrollTextIcon className="w-4 h-4" data-id="element-374" data-mp="3ohjc5w9nov2c" />;
}
function getActionColor(action: string) {
  const a = action.toLowerCase();
  if (a.includes('visita') && (a.includes('inicio') || a.includes('inicio'))) return {
    bg: 'bg-indigo-100',
    text: 'text-indigo-600',
    dot: 'bg-indigo-500',
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200'
  };
  if (a.includes('visita') && a.includes('fin')) return {
    bg: 'bg-emerald-100',
    text: 'text-emerald-600',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  };
  if (a.includes('visita')) return {
    bg: 'bg-indigo-100',
    text: 'text-indigo-600',
    dot: 'bg-indigo-500',
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200'
  };
  if (a.includes('cobro')) return {
    bg: 'bg-emerald-100',
    text: 'text-emerald-600',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  };
  if (a.includes('venta')) return {
    bg: 'bg-blue-100',
    text: 'text-blue-600',
    dot: 'bg-blue-500',
    badge: 'bg-blue-50 text-blue-700 border-blue-200'
  };
  if (a.includes('inicio de sesión')) return {
    bg: 'bg-violet-100',
    text: 'text-violet-600',
    dot: 'bg-violet-500',
    badge: 'bg-violet-50 text-violet-700 border-violet-200'
  };
  if (a.includes('cierre de sesión')) return {
    bg: 'bg-rose-100',
    text: 'text-rose-600',
    dot: 'bg-rose-500',
    badge: 'bg-rose-50 text-rose-700 border-rose-200'
  };
  return {
    bg: 'bg-slate-100',
    text: 'text-slate-600',
    dot: 'bg-slate-400',
    badge: 'bg-slate-50 text-slate-700 border-slate-200'
  };
}
function formatDateTime(date: Date) {
  const d = new Date(date);
  return {
    date: d.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }),
    time: d.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    })
  };
}
function groupByDate(logs: ActivityLog[]) {
  const groups: Record<string, ActivityLog[]> = {};
  logs.forEach(log => {
    const d = new Date(log.timestamp);
    const key = d.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
    if (!groups[key]) groups[key] = [];
    groups[key].push(log);
  });
  return groups;
}
export function BitacoraPage() {
  const {
    activityLogs,
    user
  } = useAuth();
  const [search, setSearch] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterCategory, setFilterCategory] = useState<'todos' | 'visitas' | 'crud' | 'sesiones'>('todos');
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const isAdmin = user?.rolNombre === 'Administrador';
  const getCategoryFromAction = (action: string) => {
    const a = action.toLowerCase();
    if (a.includes('visita')) return 'visitas';
    if (a.includes('sesión') || a.includes('sesion') || a.includes('inicio de ses') || a.includes('cierre de ses')) return 'sesiones';
    return 'crud';
  };
  // Unique users for filter
  const uniqueUsers = useMemo(() => {
    const users = [...new Set(activityLogs.map(l => l.userName))];
    return users.sort();
  }, [activityLogs]);
  // Show ALL activity logs including login/logout sessions
  const auditLogs = activityLogs;
  const filtered = useMemo(() => {
    return auditLogs.filter(log => {
      const matchSearch = !search || log.action.toLowerCase().includes(search.toLowerCase()) || log.userName.toLowerCase().includes(search.toLowerCase()) || log.details.toLowerCase().includes(search.toLowerCase());
      const matchUser = !filterUser || log.userName === filterUser;
      const matchCategory = filterCategory === 'todos' || getCategoryFromAction(log.action) === filterCategory;
      const logDate = new Date(log.timestamp).toISOString().split('T')[0];
      const matchDateFrom = !dateFrom || logDate >= dateFrom;
      const matchDateTo = !dateTo || logDate <= dateTo;
      return matchSearch && matchUser && matchCategory && matchDateFrom && matchDateTo;
    });
  }, [auditLogs, search, filterUser, filterCategory]);
  const grouped = useMemo(() => groupByDate(filtered), [filtered]);
  const today = new Date().toDateString();
  const todayCount = auditLogs.filter(l => new Date(l.timestamp).toDateString() === today).length;
  const withGps = auditLogs.filter(l => l.location).length;
  const crudCount = auditLogs.filter(l => getCategoryFromAction(l.action) === 'crud').length;
  const visitCount = auditLogs.filter(l => getCategoryFromAction(l.action) === 'visitas').length;
  const sessionCount = auditLogs.filter(l => getCategoryFromAction(l.action) === 'sesiones').length;
  const categoryTabs = [{
    id: 'todos',
    label: 'Todos',
    count: auditLogs.length
  }, {
    id: 'visitas',
    label: 'Visitas',
    count: visitCount
  }, {
    id: 'crud',
    label: 'Cambios',
    count: crudCount
  }, {
    id: 'sesiones',
    label: 'Sesiones',
    count: sessionCount
  }] as const;
  return <div className="space-y-6" data-id="element-375" data-mp="2sggkbv0yygvh">
      <PageHeader title="Bitácora del Sistema" subtitle="Registro de actividades y auditoría" icon={<ScrollTextIcon className="w-6 h-6" data-id="element-377" data-mp="1gyynlfht2hig" />} data-id="element-376" data-mp="2nfj0le0b9erv">
        
        <div className="flex items-center gap-2 px-4 py-2 bg-[#eef2fc] border border-[#d5e0f7] rounded-xl" data-id="element-378" data-mp="1h0rt2rgomvov">
          <ScrollTextIcon className="w-4 h-4 text-[#2952C4]" data-id="element-379" data-mp="1bysuduvw0my9" />
          <span className="text-sm font-bold text-[#1E3D96]" data-id="element-380" data-mp="3noihgxfy1h42">
            {activityLogs.length}
          </span>
          <span className="text-sm text-[#2952C4]" data-id="element-381" data-mp="3snbwiuavpg6c">registros totales</span>
        </div>
      </PageHeader>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-stagger-1" data-id="element-382" data-mp="1efwqi0vaflh">
        {[{
        label: 'Total registros',
        value: auditLogs.length,
        icon: <ScrollTextIcon className="w-5 h-5" data-id="element-383" data-mp="b5alpu5hztam" />,
        color: 'text-brand-600 bg-brand-100'
      }, {
        label: 'Hoy',
        value: todayCount,
        icon: <CalendarIcon className="w-5 h-5" data-id="element-384" data-mp="k70cqyvgwq7v" />,
        color: 'text-emerald-600 bg-emerald-100'
      }, {
        label: 'Cambios CRUD',
        value: crudCount,
        icon: <NavigationIcon className="w-5 h-5" data-id="element-385" data-mp="p3qya1q1ke0o" />,
        color: 'text-amber-600 bg-amber-100'
      }, {
        label: 'Usuarios activos',
        value: uniqueUsers.length,
        icon: <UserIcon className="w-5 h-5" data-id="element-386" data-mp="2qxq8ejrfgpbs" />,
        color: 'text-violet-600 bg-violet-100'
      }].map(stat => <div key={stat.label} className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-4 flex items-center gap-3" data-id="element-387" data-mp="wnrrk8is8m06">
          
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${stat.color}`} data-id="element-388" data-mp="16ffjsbn5y72p">
            
              {stat.icon}
            </div>
            <div data-id="element-389" data-mp="dq8jb745f7et">
              <p className="text-xl font-bold text-slate-900 font-heading" data-id="element-390" data-mp="dpp07n2ricz7">
                {stat.value}
              </p>
              <p className="text-xs text-slate-500" data-id="element-391" data-mp="8sp3w3vpsola">{stat.label}</p>
            </div>
          </div>)}
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 flex-wrap animate-stagger-2" data-id="element-392" data-mp="nmblue2wx82a">
        {categoryTabs.map(tab => <button key={tab.id} onClick={() => setFilterCategory(tab.id)} className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${filterCategory === tab.id ? 'bg-[#2952C4] text-white shadow-md' : 'bg-white border border-slate-200 text-slate-600 hover:border-[#2952C4]/30 hover:text-[#2952C4]'}`} data-id="element-393" data-mp="iro5j2i2bjys">
          
            {tab.label}
            <span className={`ml-1.5 text-xs ${filterCategory === tab.id ? 'text-white/70' : 'text-slate-400'}`} data-id="element-394" data-mp="26nm75vsulgep">
            
              {tab.count}
            </span>
          </button>)}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-4 space-y-3" data-id="element-395" data-mp="1wpol9eygnvp9">
        <div className="flex flex-col sm:flex-row gap-3" data-id="element-396" data-mp="2kgtyrngxibwe">
          <div className="relative flex-1" data-id="element-397" data-mp="2pcsk126yndsl">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-398" data-mp="2pfogsn72nwfg" />
            <input type="text" placeholder="Buscar por acción, usuario o detalle..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] focus:bg-white transition-all" data-id="element-399" data-mp="2bkmcu5lsch5a" />
            
          </div>
          {isAdmin && <select value={filterUser} onChange={e => setFilterUser(e.target.value)} className="px-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] transition-all text-slate-700" data-id="element-400" data-mp="tcsv03jmciwl">
            
              <option value="" data-id="element-401" data-mp="ody1o7o0zby8">Todos los usuarios</option>
              {uniqueUsers.map(u => <option key={u} value={u} data-id="element-402" data-mp="anm7m37vr34g">
                  {u}
                </option>)}
            </select>}
          <div className="flex items-center gap-1.5" data-id="element-403" data-mp="fgnq0l7apkss">
            <span className="text-xs text-slate-500 font-medium" data-id="element-404" data-mp="qgi2zewzk6c">Desde</span>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-2.5 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] transition-all" data-id="element-405" data-mp="odiporvgeoy" />
            
          </div>
          <div className="flex items-center gap-1.5" data-id="element-406" data-mp="3ryc7g3fnb7n3">
            <span className="text-xs text-slate-500 font-medium" data-id="element-407" data-mp="3998philmzwpy">Hasta</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-2.5 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] transition-all" data-id="element-408" data-mp="349nq572k1mcp" />
            
          </div>
        </div>

        {(search || filterUser || dateFrom || dateTo) && <div className="flex items-center gap-2" data-id="element-409" data-mp="3s4yu7dkyf77j">
            <span className="text-xs text-slate-500" data-id="element-410" data-mp="3n8rwu96mj417">
              {filtered.length} resultado(s)
            </span>
            <button onClick={() => {
          setSearch('');
          setFilterUser('');
          setDateFrom('');
          setDateTo('');
        }} className="text-xs text-[#2952C4] hover:text-[#1E3D96] font-medium" data-id="element-411" data-mp="4sphs9utvv1l">
            
              Limpiar filtros
            </button>
          </div>}
      </div>

      {/* Activity List */}
      <div className="space-y-4 animate-stagger-3" data-id="element-412" data-mp="9r95aep2lew2">
        {Object.entries(grouped).length === 0 ? <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04)] py-16 text-center animate-fade-in" data-id="element-413" data-mp="33iz4h8a17m7f">
            <ScrollTextIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" data-id="element-414" data-mp="3clhzj67ksuj4" />
            <p className="text-slate-500 font-medium" data-id="element-415" data-mp="3hc6w2zy44hp4">No hay registros</p>
            <p className="text-sm text-slate-400 mt-1" data-id="element-416" data-mp="3heb865660n0h">
              Prueba con otros filtros
            </p>
          </div> : Object.entries(grouped).map(([dateLabel, logs]) => <div key={dateLabel} data-id="element-417" data-mp="3r933118d4vrk">
              <div className="flex items-center gap-3 mb-4" data-id="element-418" data-mp="3rch508d472yh">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-xl" data-id="element-419" data-mp="3vcrdbrodq5gi">
                  <CalendarIcon className="w-3.5 h-3.5 text-slate-500" data-id="element-420" data-mp="44n6eigkyoi8" />
                  <span className="text-xs font-semibold text-slate-600 capitalize font-heading" data-id="element-421" data-mp="20z077259e3pt">
                    {dateLabel}
                  </span>
                </div>
                <div className="flex-1 h-px bg-slate-100" data-id="element-422" data-mp="1ydshnw2068da" />
                <span className="text-xs text-slate-400" data-id="element-423" data-mp="1yf3ibn05j006">
                  {logs.length} eventos
                </span>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden divide-y divide-slate-50" data-id="element-424" data-mp="1onpnvtn24qdw">
                {logs.map(log => {
            const colors = getActionColor(log.action);
            const {
              date,
              time
            } = formatDateTime(log.timestamp);
            const isExpanded = expandedLog === log.id;
            return <div key={log.id} className="group" data-id="element-425" data-mp="27dxl5v42smm6">
                      <button onClick={() => setExpandedLog(isExpanded ? null : log.id)} className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors text-left" data-id="element-426" data-mp="27gjsbtn2103v">
                    
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${colors.bg} ${colors.text}`} data-id="element-427" data-mp="vwz04ejyyfeq">
                      
                          {getActionIcon(log.action)}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-428" data-mp="2km69pzoi4x4n">
                          <div className="flex items-center gap-2 flex-wrap" data-id="element-429" data-mp="25w8h97jjhl9n">
                            <span className="text-sm font-semibold text-slate-900" data-id="element-430" data-mp="25u5h3uooabr7">
                              {log.action}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${colors.badge}`} data-id="element-431" data-mp="2ylxkzsnmvzey">
                          
                              {log.userName}
                            </span>
                          </div>
                          {log.details && <p className="text-xs text-slate-500 mt-0.5 truncate" data-id="element-432" data-mp="2tlja82unebd8">
                              {log.details}
                            </p>}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-433" data-mp="1i6nyaondyo0d">
                          {log.location && <div className="hidden sm:flex items-center gap-1 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded-lg" data-id="element-434" data-mp="1an4vsyyjg21e">
                              <NavigationIcon className="w-3 h-3 text-emerald-600" data-id="element-435" data-mp="15n0fb8xmr7b6" />
                              <span className="text-xs text-emerald-700 font-medium" data-id="element-436" data-mp="1klkmx1xkfbaw">
                                GPS
                              </span>
                            </div>}
                          <div className="text-right" data-id="element-437" data-mp="rw40ckwmmoml">
                            <p className="text-xs font-semibold text-slate-700" data-id="element-438" data-mp="ruj3gomuatlk">
                              {time}
                            </p>
                            <p className="text-xs text-slate-400" data-id="element-439" data-mp="11mothstj70y4">{date}</p>
                          </div>
                          <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-440" data-mp="wup06j7yv6ai" />
                      
                        </div>
                      </button>

                      {isExpanded && <div className="px-5 pb-4 bg-slate-50 border-t border-slate-100" data-id="element-441" data-mp="46sw2tg2ic3p">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4" data-id="element-442" data-mp="tuor2ivxkdj6">
                            <div data-id="element-443" data-mp="xvh29hod4si3">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-444" data-mp="xuffifxzihkj">
                                Usuario
                              </p>
                              <p className="text-sm text-slate-900 font-medium" data-id="element-445" data-mp="17opjyw07b6bm">
                                {log.userName}
                              </p>
                            </div>
                            <div data-id="element-446" data-mp="ey7j81my9h1d">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-447" data-mp="jxae4t97u6vm">
                                Fecha y Hora
                              </p>
                              <p className="text-sm text-slate-900 font-medium" data-id="element-448" data-mp="1vc6255tr5kou">
                                {date} a las {time}
                              </p>
                            </div>
                            {log.details && <div className="sm:col-span-2" data-id="element-449" data-mp="3ktul9ev1jjnm">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-450" data-mp="3kvdz6yt85im8">
                                  Detalles
                                </p>
                                <p className="text-sm text-slate-900" data-id="element-451" data-mp="3b1f3oinnox39">
                                  {log.details}
                                </p>
                              </div>}
                            {log.location && <div className="sm:col-span-2" data-id="element-452" data-mp="320qqtra012ry">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-453" data-mp="2x2xjuw0vg4qm">
                                  Ubicación GPS
                                </p>
                                <div className="flex items-center gap-2" data-id="element-454" data-mp="2n6uhpq60s63o">
                                  <NavigationIcon className="w-4 h-4 text-emerald-600" data-id="element-455" data-mp="qdslv9miw4os" />
                                  <span className="text-sm text-slate-900 font-mono" data-id="element-456" data-mp="leftyagso7fc">
                                    {log.location.lat.toFixed(6)},{' '}
                                    {log.location.lng.toFixed(6)}
                                  </span>
                                </div>
                              </div>}
                          </div>
                        </div>}
                    </div>;
          })}
              </div>
            </div>)}
      </div>
    </div>;
}